import React, { useState } from "react"
import { ScrollView, View } from "react-native"
import { useCombinedLoanData } from "@/queries/loan"
import { useAnalysis } from "@/queries/analysis"
import { BFScreen } from "@/components/standard/screen/BFScreen"
import { Body, H2 } from "@/components/standard/BFText"
import { Vertical } from "@/components/standard/BFLayout"
import { AnalysisHeader } from "./components/AnalysisHeader"
import { CompSelector } from "./components/CompSelector"
import FullScreenLoader from "@/components/loading/FullScreenLoaderDark"

interface LoanAnalysisScreenProps {
  loanId?: string
}

export function LoanAnalysisScreen({ loanId }: LoanAnalysisScreenProps) {
  const [viewMode, setViewMode] = useState<"list" | "map">("list")
  const [ownScenarioComps, setOwnScenarioComps] = useState<Set<string>>(
    new Set()
  )

  const { data: loanData, isLoading: loanLoading } = useCombinedLoanData(loanId)
  const analysisId = loanData?.loanApplication?.analysis?.id

  const { data: analysisData, isLoading: analysisLoading } = useAnalysis(
    analysisId || "",
    true
  )

  const isLoading = loanLoading || analysisLoading

  if (isLoading) {
    return <FullScreenLoader>Loading loan analysis...</FullScreenLoader>
  }

  if (!loanData?.loanApplication) {
    return (
      <BFScreen includeStandardSideMargins>
        <Vertical className="flex-1 justify-center items-center gap-4">
          <H2 className="text-center">Loan Not Found</H2>
          <Body className="text-center text-muted-foreground">
            The requested loan could not be found.
          </Body>
        </Vertical>
      </BFScreen>
    )
  }

  const loanApplication = loanData.loanApplication
  const analysis = analysisData?.analysis

  return (
    <BFScreen includeStandardSideMargins={false} className="bg-background">
      <ScrollView className="flex-1">
        <AnalysisHeader
          loanApplication={{
            property: {
              address: loanApplication.property?.fullAddress,
              area: loanApplication.property?.area || undefined,
            },
            purchasePrice: loanApplication.purchasePrice,
            afterRepairValue: loanApplication.afterRepairValue || undefined,
            rehabEstimate: loanApplication.rehabEstimate || undefined,
            costBasis: loanApplication.costBasis || undefined,
            grossProfit: loanApplication.grossProfit || undefined,
          }}
          analysis={analysis || undefined}
          ownScenarioComps={ownScenarioComps}
        />

        <View className="flex-1 px-5" style={{ flex: 1 }}>
          <CompSelector
            analysis={analysis}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            ownScenarioComps={ownScenarioComps}
            onOwnScenarioCompsChange={setOwnScenarioComps}
          />
        </View>
      </ScrollView>
    </BFScreen>
  )
}
